import { inject, Injectable } from '@angular/core';
import {
  EngagementFeedWithUser,
  EntityType,
} from '@epicuro-next/data/engagement/engagement-models';
import { getPromoteAvailableMediaItems } from '@epicuro-next/features/engagement/list-utils';
import { List } from '@epicuro-next/models/engagement';
import { selectOpenedListFromListEntity } from '@epicuro-next/state/list-entities';
import {
  selectDynamicLayoutBuilderEnabled,
  selectListPromoteSpotlightLayoutSchema,
} from '@epicuro-next/state/product-list-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { select, Store } from '@ngrx/store';
import { filter, switchMap, tap } from 'rxjs/operators';

interface State {
  feed: Partial<EngagementFeedWithUser> | null;
}

const initial: State = {
  feed: null,
};

@Injectable()
export class PromoteViaFeedBaseStore extends ComponentStore<State> {
  private readonly store = inject(Store);
  private readonly list$ = this.store.pipe(
    select(selectOpenedListFromListEntity),
    filter(isNonNullish),
  );
  public readonly feed$ = this.select((state) => state.feed);

  public dynamicLayoutBuilderEnabled$ = this.store.pipe(
    select(selectDynamicLayoutBuilderEnabled),
  );

  public readonly layoutSchemas$ = this.store.pipe(
    select(selectListPromoteSpotlightLayoutSchema),
  );

  public readonly availableMedias$ = this.select(
    this.list$.pipe(filter(isNonNullish)),
    getPromoteAvailableMediaItems,
  );

  constructor() {
    super(initial);
  }

  public subscribeList = this.effect<keyof List>((feedIdProperty$) => {
    return feedIdProperty$.pipe(
      switchMap((feedIdProperty) =>
        this.list$.pipe(
          tap((list: List) => {
            this.updateFeed({
              referenceId: list.id,
              type: EntityType.ProductList,
              title: list.name,
              id: list[feedIdProperty] as string,
            });
          }),
        ),
      ),
    );
  });

  public updateFeed(feed: Partial<EngagementFeedWithUser> | null) {
    this.patchState({ feed });
  }
}
